import { api } from "./apiClient";

const xhrPut = (url, body, headers = {}, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([name, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(name, value);
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    };
    xhr.onerror = () => reject(new Error("Direct S3 upload failed due to a network error."));
    xhr.onabort = () => reject(new Error("Direct S3 upload was aborted."));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader("ETag") });
      } else {
        reject(new Error(`Direct S3 upload failed (${xhr.status}).`));
      }
    };
    xhr.send(body);
  });

const retryRequest = async (fn, attempts = 3) => {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt + 1 < attempts) {
        await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
      }
    }
  }
  throw lastError;
};

// Keep multipart signing short-lived and bounded. Files are sliced in the browser,
// so the FastAPI process never receives the file bytes.
const uploadMultipart = async (projectId, file, authorization, onProgress) => {
  const partSize = Number(authorization.part_size);
  const providedParts = authorization.parts || [];
  const partCount = Number(authorization.part_count || providedParts.length);
  if (!partSize || !partCount || !authorization.upload_id) {
    throw new Error("Invalid multipart upload authorization.");
  }

  const loadedByPart = new Map();
  const completed = new Array(partCount);
  let lastPercent = -1;
  let totalLoaded = 0;

  const report = (partNumber, loaded) => {
    totalLoaded += loaded - (loadedByPart.get(partNumber) || 0);
    loadedByPart.set(partNumber, loaded);
    const percent = file.size ? Math.min(99, Math.round((totalLoaded / file.size) * 100)) : 99;
    if (percent !== lastPercent) {
      lastPercent = percent;
      onProgress?.(percent, "uploading");
    }
  };

  // Use a single wave for very large individual parts; sign smaller parts in
  // batches to reduce API traffic while avoiding stale 15-minute URLs.
  const batchSize = partSize > 64 * 1024 * 1024 ? 4 : 24;
  for (let batchStart = 0; batchStart < partCount; batchStart += batchSize) {
    const batchEnd = Math.min(partCount, batchStart + batchSize);
    let batch = providedParts.length
      ? providedParts.slice(batchStart, batchEnd)
      : (await documentsAPI.presignDirectParts(projectId, {
          object_key: authorization.object_key,
          upload_id: authorization.upload_id,
          inspection_id: authorization.inspection_id ?? null,
          part_numbers: Array.from(
            { length: batchEnd - batchStart },
            (_, index) => batchStart + index + 1,
          ),
        })).data.parts;
    if (batch.length !== batchEnd - batchStart) {
      throw new Error("Incomplete multipart part authorization.");
    }
    let cursor = 0;
    const worker = async () => {
      while (cursor < batch.length) {
        const part = batch[cursor++];
        const partNumber = Number(part.part_number);
        const start = (partNumber - 1) * partSize;
        const blob = file.slice(start, Math.min(file.size, start + partSize));
        let signedUrl = part.url;
        const response = await retryRequest(async () => {
          // A retry starts a new PUT; don't count the failed attempt's bytes.
          report(partNumber, 0);
          try {
            const result = await xhrPut(signedUrl, blob, {}, (loaded) => report(partNumber, loaded));
            if (!result.etag) {
              throw new Error("S3 did not expose the multipart ETag. Check the bucket CORS ExposeHeaders setting.");
            }
            return result;
          } catch (error) {
            // Refresh on failures because a URL can expire during a long upload.
            if (!providedParts.length) {
              try {
                const renewed = await documentsAPI.presignDirectParts(projectId, {
                  object_key: authorization.object_key,
                  upload_id: authorization.upload_id,
                  inspection_id: authorization.inspection_id ?? null,
                  part_numbers: [partNumber],
                });
                signedUrl = renewed.data.parts[0].url;
              } catch (_) {
                // Preserve the original failure for the retry/outer handler.
              }
            }
            throw error;
          }
        }, 3);
        report(partNumber, blob.size);
        completed[partNumber - 1] = { part_number: partNumber, etag: response.etag };
      }
    };
    // Finish all in-flight requests before a failure propagates to abort.
    const result = await Promise.allSettled(
      Array.from({ length: Math.min(4, batch.length) }, () => worker()),
    );
    const failure = result.find((item) => item.status === "rejected");
    if (failure) throw failure.reason;
  }
  onProgress?.(99, "finalizing");
  return completed;
};

export const documentsAPI = {
  listByProject: (projectId) => api.get(`/projects/${projectId}/documents`),

  // Kept for other legacy callers; Document Control tabs use uploadDirect.
  upload: (projectId, formData) =>
    api.post(`/projects/${projectId}/documents`, formData, {
      timeout: 120000,
      headers: { "Content-Type": "multipart/form-data" },
    }),

  initiateDirectUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/initiate`, payload),

  presignDirectParts: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/parts`, payload),

  completeDirectMultipartUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/complete-multipart`, payload, {
      timeout: 120000,
    }),

  abortDirectMultipartUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/abort`, payload),

  finalizeDirectUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/finalize`, payload, {
      timeout: 120000,
    }),

  uploadDirect: async (projectId, file, metadata = {}, onProgress) => {
    const contentType = file.type || "application/octet-stream";
    const base = {
      filename: file.name,
      content_type: contentType,
      file_size: file.size,
      inspection_id: metadata.inspection_id ?? null,
    };

    onProgress?.(0);
    const initiated = await documentsAPI.initiateDirectUpload(projectId, base);
    const authorization = initiated.data;
    let parts = null;

    try {
      if (authorization.mode === "multipart") {
        parts = await uploadMultipart(projectId, file, {
          ...authorization, inspection_id: base.inspection_id,
        }, onProgress);
      } else {
        await xhrPut(
          authorization.url,
          file,
          authorization.headers || {},
          (loaded) => {
            const percent = file.size ? Math.min(99, Math.round((loaded / file.size) * 100)) : 99;
            onProgress?.(percent, "uploading");
          },
        );
        onProgress?.(99, "finalizing");
      }
    } catch (error) {
      if (authorization.mode === "multipart" && authorization.upload_id) {
        try {
          await documentsAPI.abortDirectMultipartUpload(projectId, {
            object_key: authorization.object_key,
            upload_id: authorization.upload_id,
            inspection_id: base.inspection_id,
          });
        } catch (_) {
          // Best-effort cleanup; preserve the original upload error.
        }
      }
      throw error;
    }

    const finalizePayload = {
      ...base,
      object_key: authorization.object_key,
      document_name: metadata.document_name,
      document_type: metadata.document_type ?? null,
      category: metadata.category ?? null,
      version: metadata.version ?? null,
      status: metadata.status ?? "draft",
      rag_mode: metadata.rag_mode ?? "manual",
    };

    if (authorization.mode === "multipart") {
      try {
        await documentsAPI.completeDirectMultipartUpload(projectId, {
          object_key: authorization.object_key,
          upload_id: authorization.upload_id,
          inspection_id: base.inspection_id,
          parts,
        });
      } catch (completionError) {
        // Completion responses can be lost after S3 has already assembled the
        // object. Finalize is idempotent and HEAD-verifies the object, so use it
        // as the safe recovery path before deciding the multipart upload failed.
        try {
          const finalized = await retryRequest(
            () => documentsAPI.finalizeDirectUpload(projectId, finalizePayload),
            3,
          );
          onProgress?.(100, "completed");
          return finalized;
        } catch (_) {
          try {
            await documentsAPI.abortDirectMultipartUpload(projectId, {
              object_key: authorization.object_key,
              upload_id: authorization.upload_id,
              inspection_id: base.inspection_id,
            });
          } catch (__unused) {
            // Best effort only.
          }
          throw completionError;
        }
      }
    }

    const finalized = await retryRequest(
      () => documentsAPI.finalizeDirectUpload(projectId, finalizePayload),
      3,
    );
    onProgress?.(100, "completed");
    return finalized;
  },

  delete: (projectId, documentId) =>
    api.delete(`/projects/${projectId}/documents/${documentId}`),

  // Link an existing project document to an inspection without re-uploading it.
  // The same document may be linked to multiple inspections.
  linkToInspection: (projectId, inspectionId, documentId) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/documents/${documentId}`,
    ),

  // Remove only the inspection relationship; keep the file in Document Control.
  unlinkFromInspection: (projectId, inspectionId, documentId) =>
    api.delete(
      `/projects/${projectId}/inspections/${inspectionId}/documents/${documentId}`,
    ),

  indexForRag: (projectId, documentId, options = {}) =>
    api.post(
      `/projects/${projectId}/documents/${documentId}/rag/index`,
      options,
    ),

  indexForRagSemantic: (projectId, documentId, formData) =>
    api.post(
      `/projects/${projectId}/documents/${documentId}/rag/index-semantic`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
};
